import { SelectionModel } from '@angular/cdk/collections';
import { AfterViewInit, ChangeDetectorRef, Component, Input, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FailModalComponent } from '@shared/components/fail-modal/fail-modal.component';
import { SuccessModalComponent } from '@shared/components/success-modal/success-modal.component';
import { ProductInstanceModel } from '@shared/models/data/product-instance-model';
import { RemoveSeatsModel } from '@shared/models/data/remove-seats-model';
import { ResidentModel } from '@shared/models/data/resident-model';
import { SeatAssignmentModel } from '@shared/models/data/seat-assignment-model';
import { SeatModel } from '@shared/models/data/seat-model.component';
import { SeatUpdateResultModel } from '@shared/models/data/seat-update-result-model';
import { AuthService } from '@shared/services/auth.service';
import { EnrollmentService } from '@shared/services/enrollment.service';
import { SeatAssignmentService } from '@shared/services/seat-assignment.service';
import { SessionService } from '@shared/services/session.service';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'aaos-oite-paid-seats-table',
  templateUrl: './oite-paid-seats-table.component.html',
  styleUrls: ['./oite-paid-seats-table.component.scss']
})
export class OitePaidSeatsTableComponent implements AfterViewInit {

  updatedResidents = new Array<SeatAssignmentModel>();
  @ViewChild(MatSort) sort: MatSort;
  @Input() oiteProduct: ProductInstanceModel;
  residents: SeatAssignmentModel[];
  dataSource: MatTableDataSource<SeatAssignmentModel>;
  columnsToDisplay = ['select', 'residencyPostGraduateYear', 'firstName', 'lastName', 'id', 'institutionalEmail', 'residencyDates', 'npi'];
  editPrivileges = false;
  @Input() productId: string;
  institutionId: string;
  loading = true;
  constructor(
    private authService: AuthService,
    private enrollmentService: EnrollmentService,
    private sessionService: SessionService,
    private seatAssignmentService: SeatAssignmentService,
    private dialog: MatDialog,
    private cdRef: ChangeDetectorRef) {
    this.dataSource = new MatTableDataSource();
  }

  ngAfterViewInit() {
    this.residents = this.oiteProduct?.seatAssignments;
    this.institutionId = this.sessionService.getInstitutionId();
    this.editPrivileges = this.authService.changeAuthorized();
    this.dataSource.sort = this.sort;
    if (this.residents) {
      this.dataSource.data = JSON.parse(JSON.stringify(this.residents));
    }
    this.loading = false;
    this.cdRef.detectChanges();

  }

  isAllSelected() {
    if (this.dataSource.data && this.dataSource.data.length !== 0) {
      const allSelected = this.dataSource.data.every(resident => resident.seatAssigned === true);
      return allSelected;
    }
  }

  masterToggle() {
    if (this.isAllSelected()) {
      this.dataSource.data.forEach(resident => resident.seatAssigned = false);
      this.updatedResidents = JSON.parse(JSON.stringify(this.dataSource.data));
    } else {
      this.dataSource.data.forEach(row => row.seatAssigned = true);
      this.updatedResidents = JSON.parse(JSON.stringify(this.dataSource.data));
    }
  }

  toggleSeatAssigned(resident: SeatAssignmentModel) {
    resident.seatAssigned = !resident.seatAssigned;
    const existingResident = this.updatedResidents.find(updatedResident => resident.id === updatedResident.id);
    if (existingResident) {
      Object.assign(existingResident, resident);
    } else {
      this.updatedResidents.push(resident);
    }
  }

  updateSeats() {
    this.loading = true;
    this.updatedResidents = this.filterOnlyChangedSeats(this.updatedResidents);
    const saveSeats = this.seatAssignmentService.residentsToSaveSeatsModel(this.updatedResidents.filter(x =>
      x.seatAssigned === true), this.productId);
    const removeSeats = this.seatAssignmentService.residentsToRemoveSeatsModel(this.updatedResidents.filter(x =>
      x.seatAssigned === false), this.productId);
    forkJoin([this.enrollmentService.saveSeats(saveSeats), this.enrollmentService.removeSeats(removeSeats)]).subscribe(combinedResult => {
      const [saveSeatsResult, removeSeatsResult] = combinedResult;
      const combinedErrors = saveSeatsResult.errors.concat(removeSeatsResult.errors);
      const combinedFailedSeats = saveSeatsResult.failedSeats.concat(removeSeatsResult.failedSeats);
      this.undoFailedSeats(combinedFailedSeats);
      this.loading = false;
      const ok = (saveSeatsResult.isValid && removeSeatsResult.isValid) ? true : false;
      this.showStatusUpdateModal(ok, combinedFailedSeats, combinedErrors);
      this.updatedResidents.length = 0;
      this.residents = JSON.parse(JSON.stringify(this.dataSource.data));
    });
  }

  trimLeadingZeros(stringToReplace: string) {
    return stringToReplace.replace(/^0+/, '');
  }

  undoFailedSeats(seatModels: Array<SeatModel>) {
    this.updatedResidents.forEach(resident => {
      seatModels.forEach(seat => {
        if (resident.id === seat.residentId) {
          resident.seatAssigned = !resident.seatAssigned;
        }
      });
    });
  }

  filterOnlyChangedSeats(unfilteredResidents: SeatAssignmentModel[]) {
    const filteredResidents = new Array<SeatAssignmentModel>();
    unfilteredResidents.forEach(filteredResident => {
      const originalResident = this.residents.find(resident => filteredResident.id === resident.id);
      if (originalResident.seatAssigned !== filteredResident.seatAssigned) {
        filteredResidents.push(filteredResident);
      }
    });
    return filteredResidents;
  }

  showStatusUpdateModal(ok: boolean, seatModels: Array<SeatModel>, errors: Array<string>) {
    const resModels = this.getSeatAssignmentModel(seatModels);
    const dialogRefComponent =
      ok === true
        ? SuccessModalComponent
        : FailModalComponent;
    const dialogRef = this.dialog.open(dialogRefComponent, {
      width: '400px',
      data: {
        errorType: 'failedSeatsUpdate',
        failedResModels: resModels,
        errors: errors
      }
    });
  }

  getSeatAssignmentModel(unsavedSeats: SeatModel[]): Array<SeatAssignmentModel> {
    const seatAssignmentModels = new Array<SeatAssignmentModel>();
    unsavedSeats.forEach(seat => {
      const seatAssignmentModel = this.residents.find(resident => resident.id === seat.residentId);
      seatAssignmentModels.push(seatAssignmentModel);

    });
    return seatAssignmentModels;
  }

}
